import { Row, Typography } from "antd";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  faLocationDot,
  faPlayCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import OptionsCheckboxFilter, {
  OptionItem,
} from "components/options-checkbox-filter/OptionsCheckboxFilter";
import ApplicationStatusFilter from "components/application-status-filter/ApplicationStatusFilter";
import DropdownFilterWrapper from "components/dropdown-filter-wrapper/DropdownFilterWrapper";
import { BusinessState } from "redux-context/business";
import { useSelector } from "react-redux";
import { Venue } from "interfaces/venue";
import InfluencerCard from "pages/influencers/InfluencerCard";
import { searchBusinessApplications } from "services/SauceService";
import Loader from "components/loader/Loader";
import AppliedFIlterChips, {
  FilterChip,
} from "pages/influencers/AppliedFIlterChips";
import "./Applications.scss";

export const parseHourTo24 = (hourRaw?: string) => {
  if (!hourRaw) return "00:00:00";
  const s = hourRaw.trim().toUpperCase();

  // Check for AM/PM
  const ampmMatch = s.match(/\b(AM|PM)\b/);
  if (ampmMatch) {
    // Remove AM/PM and parse hour/minute
    const timePart = s.replace(/\s*(AM|PM)\b/, "");
    const hm = timePart.match(/^(\d{1,2})(?::(\d{2}))?/);
    if (!hm) return "00:00:00";
    let hour = parseInt(hm[1], 10);
    const minute = hm[2] || "00";
    const isPM = ampmMatch[1] === "PM";

    if (hour === 12) {
      hour = isPM ? 12 : 0;
    } else if (isPM) {
      hour += 12;
    }

    return `${hour.toString().padStart(2, "0")}:${minute}:00`;
  }

  // Assume already 24h like "12:00" or "7:30"
  const hm = s.match(/^(\d{1,2})(?::(\d{2}))?/);
  if (!hm) return "00:00:00";
  const hour = hm[1].padStart(2, "0");
  const minute = hm[2] || "00";
  return `${hour}:${minute}:00`;
};

const Applications: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { business } = useSelector((state: BusinessState) => state.business);

  const queryParams = new URLSearchParams(location.search);
  const campaignIdFilter = queryParams.get("campaignId") || "all";
  const statusFilter = queryParams.get("status") || "pending";

  const [pendingInfluencers, setPendingInfluencers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState({
    campaign: campaignIdFilter === "all" ? "" : campaignIdFilter,
    location: "",
    status: statusFilter,
  });
  const [filteredInfluencers, setFilteredInfluencers] = useState<any[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<FilterChip[]>([
    {
      key: "status",
      value: statusFilter,
    },
  ]);

  const venueOptions = useMemo((): OptionItem[] => {
    if (business) {
      return (
        business.venues?.map((venue: Venue) => ({
          value: venue.uuid,
          label: venue.name,
          description: venue.addressDisplay,
          iconClassName: "location-icon",
        })) || []
      );
    }
    return [];
  }, [business]);

  const getSortComparator = (status: string) => {
    if (status === "pending") {
      return (a: any, b: any) =>
        new Date(b.dueDateToReview).getTime() -
        new Date(a.dueDateToReview).getTime();
    }

    if (status === "accepted") {
      const getBookingTimestamp = (item: any) => {
        const date = item?.applicationDate?.date;
        const hour = item?.applicationDate?.hour;
        if (!date) return 0;

        const timeStr = parseHourTo24(hour);
        const datePart = date.includes("T") ? date.split("T")[0] : date;

        const dateTimeStr = hour ? `${datePart}T${timeStr}Z` : date;
        const ts = new Date(dateTimeStr).getTime();
        return isNaN(ts) ? 0 : ts;
      };

      return (a: any, b: any) =>
        getBookingTimestamp(a) - getBookingTimestamp(b);
    }

    return (a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

  const influencerComparator = useMemo(() => {
    const statusToUse = statusFilter || "pending";
    return getSortComparator(statusToUse);
  }, [statusFilter]);

  const fetchApplications = useCallback(async () => {
    const businessId = localStorage.getItem("businessId");
    if (!businessId) {
      setPendingInfluencers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response: any = await searchBusinessApplications({
        businessId,
        tabStatus: statusFilter,
      });
      setPendingInfluencers(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to fetch applications", error);
      setPendingInfluencers([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    setFilters((prev) => {
      const newFilters = { ...prev, status: statusFilter };
      if (pendingInfluencers.length > 0) {
        applyFilters({ campaign: prev.campaign, location: prev.location });
      }
      return newFilters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const campaignFilterOptions = useMemo((): OptionItem[] => {
    return pendingInfluencers
      .map((collab: any) => ({
        value: collab.campaign?.id,
        label: collab.campaign?.name,
        description: "Active",
        iconClassName: "campaign-icon",
      }))
      .filter((item: any) => item.value)
      .filter(
        (item: any, index: number, self: any) =>
          index === self.findIndex((t: any) => t.value === item.value),
      );
  }, [pendingInfluencers]);

  const applyFilters = useCallback(
    (filterValues?: { campaign?: string; location?: string }) => {
      const currentFilters = filterValues || filters;
      const selectedLocations = currentFilters.location
        ? currentFilters.location.split(";").filter(Boolean)
        : [];

      const selectedCampaigns = currentFilters.campaign
        ? currentFilters.campaign.split(";").filter(Boolean)
        : [];

      const isAllLocations = selectedLocations.length === 0;
      const isAllCampaigns = selectedCampaigns.length === 0;

      const influencersFiltered = pendingInfluencers
        .filter((x) => x.tabStatus !== "rejected")
        .filter((collab: any) => {
          const isMatchingVenue = selectedLocations.includes(
            collab?.venue?.uuid,
          );
          const isMatchingCampaign = selectedCampaigns.includes(
            collab.campaign?.id,
          );

          if (
            (isAllLocations || isMatchingVenue) &&
            (isAllCampaigns || isMatchingCampaign)
          ) {
            return true;
          }

          return false;
        });

      setFilteredInfluencers(influencersFiltered.sort(influencerComparator));
    },
    [pendingInfluencers, filters, influencerComparator],
  );

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInfluencers, influencerComparator]);

  const onChangeStatuFilter = (value: string) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  const getAppliedFilters = useCallback(
    (filterValues: typeof filters): FilterChip[] => {
      const chips: FilterChip[] = [];

      if (filterValues.campaign && filterValues.campaign.trim() !== "") {
        const campaignIds = filterValues.campaign
          .split(";")
          .map((id: string) => id.trim())
          .filter((id: string) => id !== "");

        campaignIds.forEach((campaignId: string) => {
          const campaign = campaignFilterOptions.find(
            (opt) => opt.value === campaignId,
          );
          chips.push({
            key: "campaign",
            value: campaign?.label || campaignId,
          });
        });
      }

      if (filterValues.location && filterValues.location.trim() !== "") {
        const locationIds = filterValues.location
          .split(";")
          .map((id: string) => id.trim())
          .filter((id: string) => id !== "");

        locationIds.forEach((locationId: string) => {
          const venue = venueOptions.find((opt) => opt.value === locationId);
          chips.push({
            key: "location",
            value: venue?.label || locationId,
          });
        });
      }

      if (filterValues.status) {
        chips.push({
          key: "status",
          value: filterValues.status,
        });
      }

      return chips;
    },
    [campaignFilterOptions, venueOptions],
  );

  const handleRemoveFilter = useCallback(
    (filter: FilterChip) => {
      setFilters((prev) => {
        const updated = { ...prev };

        if (filter.key === "campaign") {
          const campaign = campaignFilterOptions.find(
            (opt) => opt.label === filter.value,
          );
          if (campaign?.value) {
            const campaignIds = prev.campaign
              ? prev.campaign
                  .split(";")
                  .map((id: string) => id.trim())
                  .filter((id: string) => id !== "" && id !== campaign.value)
              : [];
            updated.campaign =
              campaignIds.length > 0 ? campaignIds.join(";") : "";
          }
        } else if (filter.key === "location") {
          const venue = venueOptions.find((opt) => opt.label === filter.value);
          if (venue?.value) {
            const locationIds = prev.location
              ? prev.location
                  .split(";")
                  .map((id: string) => id.trim())
                  .filter((id: string) => id !== "" && id !== venue.value)
              : [];
            updated.location =
              locationIds.length > 0 ? locationIds.join(";") : "";
          }
        } else if (filter.key === "status") {
          updated.status = "pending";
        }

        applyFilters(updated);
        setAppliedFilters(getAppliedFilters(updated));

        return updated;
      });
    },
    [campaignFilterOptions, venueOptions, applyFilters, getAppliedFilters],
  );

  return (
    <div className="applications">
      <Loader visible={loading} />
      <Row justify="space-between" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 24,
          }}
        >
          <DropdownFilterWrapper
            btnText="Campaign"
            btnIcon={faPlayCircle}
            title="Filter by Campaign"
            subtitle="Select campaigns to view applications"
            onClear={() => {
              setFilters((prev) => {
                const newFilters = { ...prev, campaign: "" };
                applyFilters(newFilters);
                setAppliedFilters(getAppliedFilters(newFilters));
                return newFilters;
              });
            }}
            onFilter={() => {
              applyFilters(filters);
              setAppliedFilters(getAppliedFilters(filters));
            }}
          >
            <OptionsCheckboxFilter
              values={filters.campaign}
              onChangeValues={(value) =>
                setFilters((prev) => ({ ...prev, campaign: value }))
              }
              options={campaignFilterOptions}
              style={{ width: 320 }}
            />
          </DropdownFilterWrapper>
          <DropdownFilterWrapper
            btnText="Location"
            btnIcon={faLocationDot}
            title="Filter by Location"
            subtitle="Select locations to view applications"
            onClear={() => {
              setFilters((prev) => {
                const newFilters = { ...prev, location: "" };
                applyFilters(newFilters);
                setAppliedFilters(getAppliedFilters(newFilters));
                return newFilters;
              });
            }}
            onFilter={() => {
              applyFilters(filters);
              setAppliedFilters(getAppliedFilters(filters));
            }}
          >
            <OptionsCheckboxFilter
              values={filters.location}
              onChangeValues={(value) =>
                setFilters((prev) => ({ ...prev, location: value }))
              }
              options={venueOptions}
              style={{ width: 380 }}
            />
          </DropdownFilterWrapper>

          <DropdownFilterWrapper
            btnText="Status"
            btnIcon={faClock}
            title="Filter by Status"
            subtitle="Select application status"
            onClear={() => {
              setFilters((prev) => {
                const newFilters = { ...prev, status: "pending" };
                setAppliedFilters(getAppliedFilters(newFilters));
                return newFilters;
              });
            }}
            onFilter={() => {
              if (filters.status) {
                navigate(`${location.pathname}?status=${filters.status}`);
              } else {
                navigate(`${location.pathname}`);
              }
              setAppliedFilters(getAppliedFilters(filters));
            }}
          >
            <ApplicationStatusFilter
              values={filters.status}
              onChangeValues={onChangeStatuFilter}
            />
          </DropdownFilterWrapper>
        </div>
      </Row>
      <Row>
        <Typography.Text>
          {filteredInfluencers.length} application
          {filteredInfluencers.length !== 1 ? "s" : ""} found
        </Typography.Text>
      </Row>
      <AppliedFIlterChips
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
      />
      <Row
        style={{
          gap: 16,
          marginTop: location.search ? 10 : 16,
          padding: "0 4px",
        }}
      >
        {filteredInfluencers.map((application: any, idx: number) => (
          <InfluencerCard
            key={`INFLUENCER_${application.foodieId}_${application.collabId}_${idx}`}
            influencer={application}
            onInfluencerClick={() => {
              navigate(`/creators/${application?.foodieId}`);
            }}
            refetch={fetchApplications}
          />
        ))}
      </Row>
    </div>
  );
};

export default Applications;
